/**
 * src/components/shared/ChatMessageList.jsx — Shared chat message renderer.
 *
 * Renders user, agent/assistant, system, and loading messages with
 * consistent styling. Used by AgentShell.
 *
 * Phase 82-01: Chat system unification.
 */

import { useRef, useEffect } from 'react';
import { Wrench, Loader2, Edit3, AlertCircle } from 'lucide-react';

const typingKeyframes = `
@keyframes chatBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
`;

// ── Styles ──────────────────────────────────────────────────
const bubbleBase = {
  maxWidth: '80%', padding: '10px 14px', borderRadius: 14,
  fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const userBubbleStyle = {
  ...bubbleBase,
  background: 'var(--navy-surface)', color: 'var(--on-navy)',
  alignSelf: 'flex-end', borderBottomRightRadius: 4,
};

const agentBubbleStyle = {
  ...bubbleBase,
  background: 'var(--glass)', color: 'var(--t1)',
  alignSelf: 'flex-start', borderBottomLeftRadius: 4,
  border: '1px solid var(--bdr)',
};

const toolChipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', borderRadius: 8, fontSize: 12,
  background: 'var(--accent-bg)', color: 'var(--accent)',
  fontWeight: 500,
};

const TOOL_LABELS = {
  search_memory: 'Searching memory',
  create_task: 'Creating task',
  update_project_status: 'Updating project',
  query_project: 'Looking up project',
  query_tasks: 'Querying tasks',
  log_action: 'Logging',
};

function TypingDots() {
  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: '80%',
      padding: '10px 14px', borderRadius: 14, borderBottomLeftRadius: 4,
      background: 'var(--glass)', border: '1px solid var(--bdr)',
      display: 'flex', alignItems: 'center', gap: 4,
    }}>
      <style>{typingKeyframes}</style>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
          background: 'var(--tm)',
          animation: `chatBounce 0.6s ease-in-out ${i * 0.15}s infinite`,
        }} />
      ))}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Array} props.messages — message objects with type, text, toolChips, provider
 * @param {boolean} props.loading — show typing indicator
 * @param {string|null} [props.error] — surface hydrate/stream errors at the top of the list
 * @param {Function} [props.onEditMessage] — called when edit button clicked on last user message
 * @param {string} [props.agentType] — 'agent' or 'assistant' (message type to match)
 * @param {Function} [props.renderAgentExtra] — (msg, index) => JSX for extra content below agent bubble (teach button, provider label)
 * @param {boolean} [props.compact] — tighter spacing for bubble mode
 * @param {Object} [props.style] — container style override
 */
export default function ChatMessageList({
  messages,
  loading,
  error,
  onEditMessage,
  agentType = 'agent',
  renderAgentExtra,
  compact = false,
  style,
  scrollRef,
}) {
  const internalRef = useRef(null);
  const containerRef = scrollRef || internalRef;

  // Auto-scroll on new messages
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const gap = compact ? 8 : 12;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        gap, paddingBottom: compact ? 8 : 16,
        ...style,
      }}
    >
      {error && (
        <div
          data-testid="chat-error-banner"
          role="alert"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 14px', borderRadius: 10,
            background: 'var(--bad-tint)', color: 'var(--bad)',
            border: '1px solid var(--bad)', fontSize: 13, lineHeight: 1.45,
          }}
        >
          <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ wordBreak: 'break-word' }}>
            {typeof error === 'string' ? error : (error?.message || 'Something went wrong loading this chat.')}
          </span>
        </div>
      )}
      {messages.map((msg, i) => {
        // ── User message ─────────────────────────────────
        if (msg.type === 'user') {
          const isLastUser = !loading && i === messages.reduce((acc, m, idx) => m.type === 'user' ? idx : acc, -1);
          return (
            <div key={msg.id || i} style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
              <div style={userBubbleStyle}>{msg.text}</div>
              {isLastUser && onEditMessage && (
                <div
                  className="edit-last-hint"
                  onClick={onEditMessage}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onEditMessage(); }}
                  style={{
                    position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)',
                    cursor: 'pointer', opacity: 0, transition: 'opacity 200ms',
                    minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Edit3 size={12} color="var(--tm)" style={{ opacity: 0.6 }} />
                </div>
              )}
            </div>
          );
        }

        // ── Agent/Assistant message ──────────────────────
        if (msg.type === agentType || msg.type === 'agent' || msg.type === 'assistant') {
          return (
            <div key={msg.id || i} style={{ display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'flex-start', maxWidth: '80%' }}>
              {msg.toolChips?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 2 }}>
                  {msg.toolChips.map((chip, ci) => (
                    <span key={ci} style={toolChipStyle}>
                      <Wrench size={12} strokeWidth={2} />
                      {TOOL_LABELS[chip.name] || chip.name}
                      {chip.status === 'running' && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                    </span>
                  ))}
                </div>
              )}
              <div style={agentBubbleStyle}>{msg.text || '...'}</div>
              {renderAgentExtra && renderAgentExtra(msg, i)}
            </div>
          );
        }

        // ── Loading ──────────────────────────────────────
        if (msg.type === 'loading') {
          return <TypingDots key={i} />;
        }

        // ── System message ───────────────────────────────
        if (msg.type === 'system') {
          return (
            <div key={msg.id || `sys-${i}`} style={{
              alignSelf: 'center', maxWidth: '80%',
              padding: '8px 14px', borderRadius: 10,
              background: 'var(--glass)', border: '1px solid var(--bdr)',
              fontSize: 13, color: 'var(--tm)', textAlign: 'center',
            }}>
              {msg.text}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
